import { Link } from 'react-router-dom';
import { HeroLogoBlue } from '@/components/brand';
import { Add } from '@/components/icons';
import { Button } from '@/components/ui/button';
import { ReportPage } from '@/pages/constants';
import { createPageUrl, getReportLink } from '@/utils';
import { HeroSlideLayout } from './HeroSlideLayout';
import { useWelcomeState } from './use-welcome-state';

const withName = (text: string, firstName: string | null) =>
    firstName ? `${text}, ${firstName}` : text;

// The mobile tour targets this button, so it has to exist in every state.
const NewReportButton = ({ label = 'Novo Laudo' }: { label?: string }) => (
    <Link to={createPageUrl(ReportPage)} className="block">
        <Button id="tour-new-report-btn" size="lg" className="w-full sm:w-auto">
            <Add className="size-4" />
            {label}
        </Button>
    </Link>
);

const useWelcomeContent = () => {
    const state = useWelcomeState();

    switch (state.kind) {
        case 'first-report':
            return {
                title: withName('Vamos ao seu primeiro laudo', state.firstName),
                description: 'É só escolher o paciente. A gente te guia no resto.',
                action: <NewReportButton label="Criar meu primeiro laudo" />,
            };

        case 'continue':
            return {
                title: withName('Bem-vindo de volta', state.firstName),
                description: `Você parou no laudo de ${state.openReport.patientName}.`,
                action: (
                    <div className="flex flex-col gap-2 sm:flex-row">
                        <Link to={getReportLink(state.openReport.id)} className="block">
                            <Button size="lg" className="w-full sm:w-auto">
                                Continuar laudo
                            </Button>
                        </Link>
                        <Link to={createPageUrl(ReportPage)} className="block">
                            <Button
                                id="tour-new-report-btn"
                                size="lg"
                                variant="outline"
                                className="w-full sm:w-auto"
                            >
                                <Add className="size-4" />
                                Novo Laudo
                            </Button>
                        </Link>
                    </div>
                ),
            };

        case 'caught-up':
            return {
                title: withName(
                    state.completed === 1
                        ? 'Você já concluiu 1 laudo'
                        : `Você já concluiu ${state.completed} laudos`,
                    state.firstName,
                ),
                description: 'Pronto para o próximo?',
                action: <NewReportButton />,
            };

        // Holds the previous copy while the summary loads so the headline doesn't flash.
        case 'loading':
        default:
            return {
                title: 'Bem-vindo de volta',
                description: 'Crie laudos profissionais com facilidade',
                action: <NewReportButton />,
            };
    }
};

export const WelcomeSlide = () => {
    const { title, description, action } = useWelcomeContent();

    return (
        <HeroSlideLayout
            as="h1"
            title={title}
            description={description}
            action={action}
            aside={<HeroLogoBlue />}
        />
    );
};
